import { Music, ThumbsUp, Users, TrendingUp, Clock, Activity, UserCheck, ShieldOff, Send, Star, ThumbsDown } from 'lucide-react';
import type { AnalyticsSummary } from '../../types/analytics';
import type { PostEventData } from '../../services/events';

interface Props {
  summary: AnalyticsSummary;
  topGenre: string;
  eventOnlyAttendees: number;
  unverifiedPlayedCount: number;
  showVerification: boolean;
  showEventOnly: boolean;
  verifiedCount?: number;
  unverifiedCount?: number;
  postEventData?: PostEventData;
}

function StatCard({ icon: Icon, label, value, color }: {
  icon: typeof Music;
  label: string;
  value: string | number;
  color: string;
}) {
  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

export default function EventSummaryCard({
  summary, topGenre, eventOnlyAttendees, unverifiedPlayedCount,
  showVerification, showEventOnly, verifiedCount, unverifiedCount, postEventData,
}: Props) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Music}
          label="Total Requests"
          value={summary.totalRequests}
          color="bg-primary/10 text-primary"
        />
        <StatCard
          icon={ThumbsUp}
          label="Total Votes"
          value={summary.totalVotes}
          color="bg-success/10 text-success"
        />
        <StatCard
          icon={Users}
          label="Unique Attendees"
          value={summary.uniqueAttendees}
          color="bg-warning/10 text-warning"
        />
        <StatCard
          icon={TrendingUp}
          label="Top Genre"
          value={topGenre}
          color="bg-secondary/10 text-secondary"
        />
        {summary.eventDurationMinutes > 0 && (
          <StatCard
            icon={Clock}
            label="Event Duration"
            value={`${Math.floor(summary.eventDurationMinutes / 60)}h ${summary.eventDurationMinutes % 60}m`}
            color="bg-indigo-500/10 text-indigo-500"
          />
        )}
        <StatCard
          icon={Activity}
          label="Engagement Rate"
          value={`${summary.engagementRate}%`}
          color="bg-pink-500/10 text-pink-500"
        />
        {showEventOnly && (
          <StatCard
            icon={UserCheck}
            label="Event-Only Attendees"
            value={eventOnlyAttendees}
            color="bg-teal-500/10 text-teal-500"
          />
        )}
        {showVerification && unverifiedPlayedCount >= 0 && (
          <StatCard
            icon={ShieldOff}
            label="Unverified Played"
            value={unverifiedPlayedCount}
            color="bg-amber-500/10 text-amber-500"
          />
        )}
      </div>

      {/* Post-Event Data Cards */}
      {postEventData?.guestCount != null && (
        <div className="grid grid-cols-2 gap-4">
          <StatCard
            icon={Users}
            label="Guests"
            value={postEventData.guestCount}
            color="bg-teal-500/10 text-teal-500"
          />
          <StatCard
            icon={TrendingUp}
            label="Participation Rate"
            value={`${Math.round((summary.uniqueAttendees / postEventData.guestCount) * 100)}%`}
            color="bg-emerald-500/10 text-emerald-500"
          />
        </div>
      )}
      {postEventData?.venueName && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Venue: <span className="font-medium text-gray-700 dark:text-gray-300">{postEventData.venueName}</span>
          {postEventData.venueType && ` (${postEventData.venueType})`}
        </p>
      )}
      {postEventData?.notes && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-400 italic">
          {postEventData.notes}
        </div>
      )}

      {/* Attendee Breakdown Strip */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Attendee Breakdown</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Send className="w-4 h-4 text-blue-500" />
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">{summary.uniqueRequesters}</p>
              <p className="text-xs text-gray-500">Requesters</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <ThumbsUp className="w-4 h-4 text-green-500" />
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">{summary.uniqueVoters}</p>
              <p className="text-xs text-gray-500">Voters</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Star className="w-4 h-4 text-amber-500" />
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">{summary.uniqueRankers}</p>
              <p className="text-xs text-gray-500">Rankers</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <ThumbsDown className="w-4 h-4 text-red-500" />
            <div>
              <p className="font-semibold text-gray-900 dark:text-white">{summary.totalDownvotes}</p>
              <p className="text-xs text-gray-500">Downvotes</p>
            </div>
          </div>
        </div>
        {showVerification && (verifiedCount !== undefined || unverifiedCount !== undefined) && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm">
              <UserCheck className="w-4 h-4 text-green-500" />
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">{verifiedCount ?? 0}</p>
                <p className="text-xs text-gray-500">On-site (verified)</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <ShieldOff className="w-4 h-4 text-gray-400" />
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">{unverifiedCount ?? 0}</p>
                <p className="text-xs text-gray-500">Off-site (unverified)</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
